import React, { useState, useRef } from "react";

import { balanceEnquiry, cashWithdrawal, miniStatement } from "../../../api/aepsApi";
import { getLocation } from "../../../components/Navigator/Navigator";

import Main from "../../Main";
import AppBreadcrumbs from "../../../components/ui/AppBreadcrumbs";
import { useInactivityTimeout } from "../../../hooks/useInactivityTimeout";
import MiniStatementPopup from "./MiniStatementPopup";
import { useBankList } from "../../../context/authBankContext";
import FingerprintModal from "../../../hooks/FingerprintModal";
import { useFingerprintCapture } from "../../../hooks/useFingerprintCapture";
import { useNotify } from "../../../context/NotifyContext";
import { useDispatch, useSelector } from "react-redux";
import { setLocation } from "../../../features/authSlice";
import AepsPipeSelect from "../../../components/AepsPipeSelect";

const MiniStatement = () => {
  const { notify } = useNotify(); // ✅ Use notify instead of showToastMessage
  const { banklist, aepsloading } = useBankList();
  const { pidData, fpStatus, fingerLoading, step, error, captureFingerprint, resetFingerprint } =
    useFingerprintCapture();

  const [form, setForm] = useState({ aadhaar: "", mobile: "", bankiin: "" });
  const [loading, setLoading] = useState(false);
  const [amountLoading, setAmountLoading] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const fetchedRef = useRef(false);
    const { location: savedLocation } = useSelector((state) => state.auth); // get location from Redux
      const pipe = useSelector((s) => s.aeps.pipe); // get location from Redux
const dispatch = useDispatch();
const [fetchingLocation, setFetchingLocation] = useState(false);
  // 🔐 Clear session securely
  const clearSession = () => {
    setForm({ aadhaar: "", mobile: "", bankiin: "" });
    resetFingerprint();
    setBalanceData(null);
    fetchedRef.current = false;
  };

  // ⏳ Auto session expire (10 min)
  useInactivityTimeout(() => {
    if (balanceData) {
      clearSession();
      notify("Session expired due to inactivity", "info");
    }
  }, 10 * 60 * 1000);

  // ✅ Form validation
  const validateForm = () => {
    const { aadhaar, mobile, bankiin } = form;

    if (!aadhaar || !/^\d{12}$/.test(aadhaar)) {
      notify("Enter a valid 12-digit Aadhaar", "error");
      return false;
    }

    if (!mobile || !/^\d{10}$/.test(mobile)) {
      notify("Enter a valid 10-digit mobile number", "error");
      return false;
    }

    if (!bankiin) {
      notify("Select a bank", "error");
      return false;
    }

      if (!pipe) {
    notify("Please select BankPipe", "error");
    return false;
  }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if ((name === "aadhaar" || name === "mobile") && !/^\d*$/.test(value)) return;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // 👉 Submit Mini Statement securely
  const submit = async (pid) => {
    if (!validateForm()) return;
    if (!pid) return notify("Fingerprint required", "error");
    if (fetchedRef.current) return;

    try {
      setLoading(true);
   setFetchingLocation(true);
  let location;

  try {
    // ✅ Check Redux first
    if (savedLocation) {
      location = savedLocation;
    } else {
      // ✅ If not in Redux, fetch geolocation
      location = await getLocation();
      dispatch(setLocation(location)); // store in Redux for future use
    }
  } catch {
    setFetchingLocation(false);
    return notify("Location permission required", "error");
  }

  setFetchingLocation(false);
      const payload = {
        ...form,
        pidData: pid,
        latitude: location.latitude,
        longitude: location.longitude,
      };

      const result = await miniStatement(payload);
      if (result?.data?.status) {
        setBalanceData(result.data);
        fetchedRef.current = true;
        notify("Mini statement fetched successfully", "success");
      } else {
        notify(result?.data?.message || "Mini statement fetch failed", "error");
      }
    } catch (err) {
      notify("Server error", "error");
    } finally {
      setLoading(false);
    }
  };

  // 👉 Handle Cash Withdrawal securely
  const handleAmountFromPopup = async (amount) => {
    if (!amount) return notify("Amount is required", "error");
    if (!validateForm()) return;
    if (!pidData) return notify("Fingerprint required", "error");

    const { aadhaar, mobile, bankiin } = form;

    setAmountLoading(true);
    try {
      const location = await getLocation();
      const payload = {
        aadhaar,
        mobile,
        bankiin: balanceData?.bankiin || bankiin,
        pidData,
        amount,
        pipe,
        latitude: location.latitude,
        longitude: location.longitude,
      };

      const result = await cashWithdrawal(payload);
      if (result?.data) {
        notify("Cash withdrawal successful", "success");
        clearSession();
      } else {
        notify(result?.data?.message || "Cash withdrawal failed", "error");
      }
    } catch (err) {
      notify("Server error", "error");
    } finally {
      setAmountLoading(false);
    }
  };

  // 👉 Capture fingerprint and fetch Mini Statement
  const handleAddBeneficiaryClick = async () => {
    if (!validateForm()) return;

    try {
      const pid = await captureFingerprint(pipe); // 🔥 Opens modal
      if (!pid) return;
      await submit(pid);
    } catch (err) {
      notify("Fingerprint capture failed", "error");
    }
  };

  const handleBankChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, bankiin: value }));
  };

  return (
    <Main showRightPanel={false}>
      <AppBreadcrumbs
        items={[
          { label: "Dashboard", to: "/" },
          { label: "Aadhaar" },
          { label: "Mini Statement" },
        ]}
      />

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="ds-card w-full max-w-sm h-fit">
          <h2 className="text-sm font-semibold mb-4 text-[var(--text-dark)]">Mini Statement Enquiry</h2>

          <form className="space-y-4">
            {fpStatus === "success" && !balanceData && (
              <div className="make-text-primary text-sm">✔ Fingerprint verified, fetching data...</div>
            )}

            {fpStatus === "error" && <div className="text-[var(--error-color)] text-sm">{error}</div>}


<AepsPipeSelect />
            <select
              className="ds-input text-sm"
              value={form.bankiin}
              onChange={handleBankChange}
              disabled={aepsloading}
            >
              <option value="">{aepsloading ? "Loading Banks..." : "Select Bank Name"}</option>
              {banklist.map((bank, index) => (
                <option key={`${bank.iinno}-${index}`} value={bank.iinno}>
                  {bank.bankName}
                </option>
              ))}
            </select>

            <div>
              <label className="ds-label">Aadhaar Number</label>
              <input
                name="aadhaar"
                required
                maxLength={12}
                value={form.aadhaar}
                onChange={handleChange}
                className="ds-input"
                placeholder="Aadhaar Number"
              />
            </div>

            <div>
              <label className="ds-label">Mobile Number</label>
              <input
                name="mobile"
                required
                maxLength={10}
                value={form.mobile}
                onChange={handleChange}
                className="ds-input"
                placeholder="Mobile Number"
              />
            </div>

            <button
              type="button"
              className="ds-btn ds-btn-primary w-full"
              onClick={handleAddBeneficiaryClick}
              disabled={fingerLoading || loading || balanceData}
            >
              {fingerLoading ? "Capturing Fingerprint..." : loading ? "Don't Refresh..." : "Check Balance"}
            </button>
          </form>
        </div>

        <FingerprintModal open={fingerLoading} step={step} />

        {balanceData && (
          <MiniStatementPopup
            details={balanceData}
            close={clearSession}
            onSubmitAmount={handleAmountFromPopup}
            loading={amountLoading}
          />
        )}
      </div>
    </Main>
  );
};

export default MiniStatement;
